import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Button } from "./ui/button";
import { Download, X } from "lucide-react";

const STORAGE_KEY = "binshare_install_dismissed";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Never show the PWA install prompt inside the native Capacitor app — the
    // user is already using the installed app, and Apple App Review flags
    // "Add to Home Screen" banners inside native binaries (guideline 2.1).
    if (Capacitor.isNativePlatform()) return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    const ua = navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    if (ios) {
      setIsIos(true);
      setTimeout(() => setOpen(true), 2500);
      return;
    }
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") dismiss();
    else setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 fade-up"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="bg-[#1B3624] text-white rounded-2xl p-5 shadow-2xl flex gap-3 items-start">
        <div className="w-10 h-10 rounded-xl bg-[#D67B54] flex items-center justify-center shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium" style={{ fontFamily: "Outfit" }}>Install Binshare</div>
          {isIos ? (
            <p className="text-xs text-white/75 mt-1 leading-relaxed">
              Tap <span className="inline-block px-1.5 py-0.5 rounded bg-white/15">Share</span> then <span className="font-medium">Add to Home Screen</span> for the full app.
            </p>
          ) : (
            <p className="text-xs text-white/75 mt-1">Add Binshare to your home screen for offline access and faster launch.</p>
          )}
          <div className="mt-3 flex gap-2">
            {!isIos && (
              <Button data-testid="install-confirm" size="sm" onClick={install} className="rounded-full bg-[#D67B54] hover:bg-[#c66c44] text-white">
                Install
              </Button>
            )}
            <Button data-testid="install-dismiss" size="sm" variant="ghost" onClick={dismiss} className="rounded-full text-white/75 hover:text-white hover:bg-white/10">
              Not now
            </Button>
          </div>
        </div>
        <button data-testid="install-close" onClick={dismiss} className="text-white/50 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
